import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { getTheme, defaultTheme } from '@/lib/themes';
import { applyTheme } from '@/lib/themes/applyTheme';
import { getDomainConfig } from '@/config/domainConfig';
import type { ThemeDefinition, ThemeAssets } from '@/lib/themes/types';

const ThemeContext = createContext<ThemeDefinition>(defaultTheme);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Domain is constant for the session — resolve once
  const resolvedId = getDomainConfig().themeId;
  const theme = getTheme(resolvedId) || defaultTheme;

  const appliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (appliedRef.current !== resolvedId) {
      applyTheme(resolvedId);
      document.title = theme.pageTitle;
      appliedRef.current = resolvedId;
    }
  }, [resolvedId, theme.pageTitle]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Get the current theme's brand assets (logos, avatars) */
export function useThemeAssets(): ThemeAssets {
  return useContext(ThemeContext).assets;
}

/** Get the full current theme definition */
export function useCurrentTheme(): ThemeDefinition {
  return useContext(ThemeContext);
}
